import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Map, Skull, Database, Code, BrainCircuit, Server, Zap, CheckCircle } from 'lucide-react';
import { useGame } from '../context/GameContext';

// ---------- WORLDS ----------
const WORLDS = [
  { start: 1, end: 10, name: "The SELECT Sector", key: "emerald", icon: <Database size={18} /> },
  { start: 11, end: 20, name: "Valley of JOINS", key: "blue", icon: <Database size={18} /> },
  { start: 21, end: 30, name: "Aggregation Peaks", key: "purple", icon: <Database size={18} /> },
  { start: 31, end: 40, name: "Python Plains", key: "yellow", icon: <Code size={18} /> },
  { start: 41, end: 50, name: "The Loop Lagoon", key: "orange", icon: <Code size={18} /> },
  { start: 51, end: 60, name: "Object Oriented Ocean", key: "cyan", icon: <Code size={18} /> },
  { start: 61, end: 70, name: "Neural Network Nebula", key: "pink", icon: <BrainCircuit size={18} /> },
  { start: 71, end: 80, name: "Deep Learning Dimension", key: "red", icon: <BrainCircuit size={18} /> },
  { start: 81, end: 90, name: "System Design Space", key: "indigo", icon: <Server size={18} /> },
  { start: 91, end: 100, name: "God Mode Galaxy", key: "rose", icon: <Zap size={18} /> },
];

// ---------- LEVEL NAMES ----------
const LEVEL_NAMES = [
  "Hello World", "Select Star", "Column Picker", "Distinct Views", "Where Clause",
  "Limit Breaker", "Order By ASC", "Order By DESC", "Null Hunter", "BOSS: The Filter",
  "Inner Circle", "Left Behind", "Right Path", "Full House", "Cross Roads",
  "Self Reflection", "Natural Bond", "Union Station", "Intersect", "BOSS: Cartesian Chaos",
  "Count Down", "Sum It Up", "Average Joe", "Max Power", "Min Value",
  "Group Therapy", "Having Fun", "Rollup", "Cube", "BOSS: The Aggregate",
  "Print Works", "Variable Valley", "String Theory", "List Logic", "Tuple Town",
  "Dict Domain", "Set Setup", "Type Cast", "Input Output", "BOSS: Syntax Error",
  "If Else", "Elif Path", "For Loop", "While Loop", "Break Out",
  "Continue On", "Range Rover", "Enumerate", "Zip Line", "BOSS: Infinite Loop",
  "Class Act", "Object Inst", "Self Aware", "Init Start", "Inheritance",
  "Poly Morph", "Encapsulation", "Super Power", "Static Shock", "BOSS: Spaghetti Code",
  "Perceptron", "Activation", "Sigmoid Slope", "ReLU Ramp", "Bias Shift",
  "Weight Watcher", "Feed Forward", "Back Prop", "Loss Function", "BOSS: Overfitting",
  "Gradient Descent", "Learning Rate", "Epoch Era", "Batch Size", "Dropout",
  "Conv Layer", "Pool Party", "Flatten", "Dense Layer", "BOSS: Vanishing Gradient",
  "Load Balancer", "Caching Cache", "Database Shard", "Replication", "Microservice",
  "Message Queue", "API Gateway", "Rate Limiter", "Consistent Hash", "BOSS: Single Point of Failure",
  "Quantum Query", "AI Architect", "Big Data", "Cloud Native", "Serverless",
  "Container", "Orchestrator", "DevOps", "Full Stack", "FINAL BOSS: The Singularity"
];

// ---------- SAFE THEME MAP (NO TAILWIND DYNAMIC CLASSES) ----------
const WORLD_THEME = {
  emerald: { glow: 'rgba(16,185,129,0.55)', chipBg: 'rgba(16,185,129,0.12)', text: '#6EE7B7' },
  blue:    { glow: 'rgba(59,130,246,0.55)', chipBg: 'rgba(59,130,246,0.12)', text: '#93C5FD' },
  purple:  { glow: 'rgba(168,85,247,0.60)', chipBg: 'rgba(168,85,247,0.12)', text: '#D8B4FE' },
  yellow:  { glow: 'rgba(234,179,8,0.55)', chipBg: 'rgba(234,179,8,0.12)', text: '#FDE68A' },
  orange:  { glow: 'rgba(249,115,22,0.55)', chipBg: 'rgba(249,115,22,0.12)', text: '#FDBA74' },
  cyan:    { glow: 'rgba(34,211,238,0.60)', chipBg: 'rgba(34,211,238,0.12)', text: '#A5F3FC' },
  pink:    { glow: 'rgba(236,72,153,0.55)', chipBg: 'rgba(236,72,153,0.12)', text: '#F9A8D4' },
  red:     { glow: 'rgba(239,68,68,0.60)', chipBg: 'rgba(239,68,68,0.12)', text: '#FCA5A5' },
  indigo:  { glow: 'rgba(99,102,241,0.60)', chipBg: 'rgba(99,102,241,0.12)', text: '#C7D2FE' },
  rose:    { glow: 'rgba(244,63,94,0.60)', chipBg: 'rgba(244,63,94,0.12)', text: '#FDA4AF' },
};

const generateLevels = () => {
  const levels = [];
  for (let i = 1; i <= 100; i++) {
    const world = WORLDS.find(w => i >= w.start && i <= w.end);
    const isBoss = i % 10 === 0;
    const name = LEVEL_NAMES[i - 1] || `Level ${i}`;
    const desc = isBoss ? "Defeat the Boss to advance." : "Clearance required.";

    levels.push({ id: i, reqLevel: i, name, desc, world, isBoss });
  }
  return levels;
};

const LEVELS = generateLevels();

export default function LevelMapPage() {
  const navigate = useNavigate();
  const { user } = useGame();
  const playerLevel = user?.level || 1;
  const currentLevelRef = useRef(null);

  useEffect(() => {
    if (currentLevelRef.current) {
      currentLevelRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  return (
    <div className="relative h-full w-full bg-black overflow-hidden flex flex-col">

      {/* ===== CYBERPUNK BACKGROUND (same universe) ===== */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%]
          bg-[radial-gradient(circle,rgba(139,92,246,0.35),rgba(0,0,0,0.9)_55%)]"
          animate={{ rotate: 360 }}
          transition={{ duration: 220, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute inset-0
          bg-[linear-gradient(120deg,transparent,rgba(34,211,238,0.25),transparent)]"
          animate={{ x: ['-120%', '120%'] }}
          transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute inset-0 opacity-70
          bg-[radial-gradient(circle_at_50%_80%,rgba(34,211,238,0.14),transparent_60%)]"
          animate={{ scale: [1, 1.25, 1] }}
          transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_35%,rgba(0,0,0,0.96)_100%)]" />
      </div>

      {/* ===== STICKY HUD ===== */}
      <div className="sticky top-0 z-20 bg-black/70 backdrop-blur-xl border-b border-cyan-400/20 px-6 py-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <h1 className="text-3xl font-black tracking-widest uppercase flex items-center gap-2 text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]">
            <Map /> Campaign Network
          </h1>
          <div className="flex items-center gap-3 bg-black/50 border border-cyan-400/40 px-4 py-2 rounded-full text-cyan-200 font-mono text-sm">
            Current Clearance: <span className="text-white font-bold">LVL {playerLevel}</span>
          </div>
        </div>
      </div>

      {/* ===== CONTENT SCROLLER ===== */}
      <div className="relative z-10 flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 py-8 pb-24">

          {WORLDS.map((world) => {
            const t = WORLD_THEME[world.key] || WORLD_THEME.cyan;

            return (
              <div key={world.name} className="mb-10">
                {/* Sector Header */}
                <div
                  className="rounded-2xl border border-cyan-400/15 bg-slate-950/40 backdrop-blur-md p-4 flex items-center gap-3"
                  style={{ boxShadow: `0 0 24px ${t.glow}` }}
                >
                  <div
                    className="p-3 rounded-xl border"
                    style={{
                      borderColor: 'rgba(255,255,255,0.08)',
                      background: t.chipBg,
                      color: t.text,
                      boxShadow: `0 0 18px ${t.glow}`
                    }}
                  >
                    {world.icon}
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-black uppercase tracking-widest" style={{ color: t.text }}>
                      Sector {world.start}-{world.end}
                    </div>
                    <div className="text-xl md:text-2xl font-black uppercase tracking-wider text-white">
                      {world.name}
                    </div>
                  </div>
                  <div
                    className="text-[10px] font-black uppercase tracking-widest px-3 py-2 rounded-full border"
                    style={{ borderColor: 'rgba(34,211,238,0.25)', background: 'rgba(0,0,0,0.35)', color: 'rgba(165,243,252,0.9)' }}
                  >
                    Network Nodes
                  </div>
                </div>

                {/* Nodes Grid */}
                <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-4">
                  {LEVELS.filter(l => l.id >= world.start && l.id <= world.end).map((level) => {
                    const isUnlocked = playerLevel >= level.reqLevel;
                    const isCurrent = playerLevel === level.reqLevel;
                    const isCompleted = playerLevel > level.reqLevel;

                    const baseBorder = isUnlocked ? 'rgba(34,211,238,0.18)' : 'rgba(148,163,184,0.08)';
                    const baseBg = isUnlocked ? 'rgba(2,6,23,0.55)' : 'rgba(2,6,23,0.35)';

                    return (
                      <motion.div
                        key={level.id}
                        ref={isCurrent ? currentLevelRef : null}
                        whileHover={isUnlocked ? { scale: 1.03 } : {}}
                        onClick={() => isUnlocked && navigate('/')}
                        className={`relative aspect-square rounded-2xl border backdrop-blur-md p-3 flex flex-col items-center justify-center overflow-hidden
                          ${isUnlocked ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}
                        `}
                        style={{
                          borderColor: baseBorder,
                          background: baseBg,
                          boxShadow: isCurrent
                            ? `0 0 28px ${t.glow}`
                            : isCompleted
                              ? `0 0 18px rgba(16,185,129,0.35)`
                              : 'none'
                        }}
                      >
                        {/* circuit rail */}
                        <div className="absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b from-purple-500/60 via-cyan-400/40 to-transparent" />

                        {/* scanline for current/unlocked */}
                        {isUnlocked && (
                          <motion.div
                            className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(34,211,238,0.06),transparent)]"
                            animate={{ y: ['-120%', '120%'] }}
                            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                          />
                        )}

                        {/* Boss beacon */}
                        {level.isBoss && (
                          <>
                            <motion.div
                              className="absolute -top-3 -right-3 bg-red-500 text-black p-2 rounded-full border border-red-900 shadow-lg z-20"
                              animate={{ y: [0, -2, 0] }}
                              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                            >
                              <Skull size={16} />
                            </motion.div>
                            <motion.div
                              className="absolute inset-0 border border-red-500/25 rounded-2xl"
                              animate={{ opacity: [0.15, 0.4, 0.15] }}
                              transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                            />
                          </>
                        )}

                        {/* Node core */}
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center font-black text-sm border"
                          style={{
                            borderColor: isCurrent ? t.text : isCompleted ? 'rgba(16,185,129,0.55)' : baseBorder,
                            background: isCurrent
                              ? `radial-gradient(circle at center, ${t.text} 0%, rgba(0,0,0,0.75) 70%)`
                              : isCompleted
                                ? 'rgba(16,185,129,0.18)'
                                : 'rgba(15,23,42,0.55)',
                            color: isCurrent ? 'black' : isCompleted ? '#6EE7B7' : 'rgba(226,232,240,0.7)',
                            boxShadow: isCurrent ? `0 0 22px ${t.glow}` : 'none'
                          }}
                        >
                          {isCompleted ? <CheckCircle size={18} /> : level.id}
                        </div>

                        <div className="mt-3 text-center w-full relative">
                          <div className={`text-[10px] font-black uppercase tracking-widest ${isCurrent ? 'text-white' : 'text-slate-400'} truncate`}>
                            {level.name}
                          </div>

                          {isCurrent && (
                            <motion.div
                              className="mt-2 inline-flex text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full border"
                              style={{ borderColor: 'rgba(34,211,238,0.35)', background: 'rgba(0,0,0,0.35)', color: 'rgba(165,243,252,0.95)' }}
                              animate={{ opacity: [0.6, 1, 0.6] }}
                              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                            >
                              CURRENT NODE
                            </motion.div>
                          )}
                        </div>

                        {/* Hover overlay: Unlocked info */}
                        {isUnlocked && (
                          <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity bg-black/85 p-3 flex flex-col items-center justify-center text-center z-20">
                            <div className="text-xs font-black uppercase tracking-widest text-cyan-300">
                              {isCompleted ? 'COMPLETED' : isCurrent ? 'READY' : 'UNLOCKED'}
                            </div>
                            <div className="mt-2 text-[10px] text-slate-200">
                              {level.desc}
                            </div>
                            <div className="mt-3 text-[9px] uppercase tracking-widest border border-slate-700 px-2 py-1 rounded text-slate-300">
                              Click to Play
                            </div>
                          </div>
                        )}

                        {/* Locked overlay hint */}
                        {!isUnlocked && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-[10px] font-black uppercase tracking-widest text-slate-500">
                            Locked
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}

        </div>
      </div>
    </div>
  );
}
